import { Modal } from '@root/src/components/common';
import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useUrlChange } from '@root/src/utils/url/useUrlChange';
import { CvDetailHub } from '../../cv/CvDetailHub';

export interface ChatTalentModalProps {
  talentsArray: {
    items: ApiSchema<'ResumeCardData'>[] | undefined
  }[] | undefined
}

// Read the selectedCvSlug query param out of a (possibly relative) URL.
const checkSelectedCvSlug = (url: string) => {
  const absoluteUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  return new URL(absoluteUrl).searchParams.get('selectedCvSlug');
};

// Strip selectedCvSlug from the current URL, returning the bare chat-list path. Used as the
// close target so the close button always returns to the chat results — even after a refresh
// that landed on the page with the param still in the URL.
const getListUrlWithoutCvSlug = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('selectedCvSlug');
  return `${url.pathname}${url.search}${url.hash}`;
};

const ChatTalentModal = (props: ChatTalentModalProps) => {
  // Destructure props
  const { talentsArray } = props;

  const openTalentModal = useCallback((userSlug: string) => {
    // Stay on the current chat page; only swap the selectedCvSlug param (used for in-modal
    // prev/next navigation). window.location.pathname is already locale-prefixed.
    const params = new URLSearchParams(window.location.search);
    params.set('selectedCvSlug', userSlug);
    window.history.pushState(null, '', `${window.location.pathname}?${params.toString()}`);
  }, []);


  const [selectedCvSlugQueryParam, setSelectedCvSlug] = useState<string | null>(null);

  // On mount, open the modal if the URL already carries a selectedCvSlug (refresh, or a
  // middle-click that opened the page in a new tab). Done in an effect to avoid an SSR/CSR
  // hydration mismatch on the conditionally-rendered modal.
  useEffect(() => {
    setSelectedCvSlug(checkSelectedCvSlug(window.location.href));
  }, []);

  // Detect URL changes and check for selectedCvSlug
  useUrlChange((newUrl: string) => {
    const cvSlug = checkSelectedCvSlug(newUrl);
    setSelectedCvSlug(cvSlug); // Update the state with the slug
  });

  const [currentListUrl] = useState<string>(typeof window !== 'undefined' ? getListUrlWithoutCvSlug() : '');

  const closeCvModal = () => {
    // Use window.history.replaceState to update the URL without adding a new history entry
    window.history.replaceState(null, '', currentListUrl);
    setSelectedCvSlug(null);
  };

  return (
    <>
      {selectedCvSlugQueryParam && (
        <Modal
          isOpen
          onClose={closeCvModal}
          size="full"
          className="!transform-none"
          body={(
            <>
              <CvDetailHub
                slug={selectedCvSlugQueryParam}
                isAuthenticated
                closeModal={closeCvModal}
                swrTalentList={talentsArray}
                openTalentModal={openTalentModal}
                size={talentsArray?.length}
                showLoadMore={false}
              />
            </>
          )}
        />
      )}
    </>
  );
};

export default ChatTalentModal;
